import React from "react";
//#region types
import { FileUploaderExtraProps, FormFieldProps } from "models/propTypes/genericFormProps";
import { FileEnums } from "models/enums";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { MaxFileSize } from "utils/constants/fileConstants";
//#endregion
//#region components
import useFileViewerContext from "../../fileViewer/context/useContext";
import FileViewerContainer from "../../fileViewer/fileViewerContainer";
import * as FileUploader from "../baseFields/fileUploader";
import { Portal } from "@mui/material";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { UtilsHelper } from "utils/helpers";

function FileUploaderFormField<T extends Record<string, any>>(props: FormFieldProps<T>) {
  const { name, field, value, disabled, handleOnChangeCustom, handleOnBlurCustom, readonly } = props;

  const { setUploadedFileDimensions } = useFileViewerContext();
  const extraProps = (field.extraProps ?? {}) as FileUploaderExtraProps;
  const previewElement = extraProps?.filePreviewElementId
    ? document.getElementById(extraProps.filePreviewElementId)
    : undefined;

  const onFilesUpdated = (uploadedFiles?: UploadedFile[]) => {
    handleOnChangeCustom(name, uploadedFiles);
    handleOnBlurCustom(name);
  };

  const onCancelUpload = (index: number) => {
    if (index > -1) {
      const updatedFileList: UploadedFile[] = [...value];
      updatedFileList.splice(index, 1);
      handleOnChangeCustom(name, updatedFileList);

      if (updatedFileList.length === 0) {
        setUploadedFileDimensions();
      }
    }
  };

  React.useEffect(() => {
    const filesMissingContent = value?.filter((file: UploadedFile) => UtilsHelper.isEmpty(file.data));
    if (filesMissingContent?.length && extraProps.loadFileContent) {
      filesMissingContent.forEach((file: UploadedFile, index: number) => {
        if (file.id) {
          extraProps.loadFileContent!(file.id).then((result) => {
            handleOnChangeCustom(`${name}[${index}].data`, result);
          });
        }
      });
    }
  }, [value]);

  return (
    <>
      {previewElement && value?.length ? (
        <Portal container={previewElement}>
          <FileViewerContainer
            meta={value.map(
              (file: UploadedFile): ImageMeta => ({
                id: file.id ?? 0,
                data: file.data,
                contentType: file.contentType,
                numberOfPages: 1,
              })
            )}
            onDeleteClick={onCancelUpload}
            className={cssExports.uploaderPreviewContainer}
          />
        </Portal>
      ) : (
        <></>
      )}

      <FileViewerContainer
        meta={value?.map(
          (file: UploadedFile): ImageMeta => ({
            id: file.id ?? 0,
            data: file.data,
            contentType: file.contentType,
            numberOfPages: 1,
          })
        )}
        onDeleteClick={onCancelUpload}
        className={cssExports.uploaderPreviewContainer}
      >
        <FileUploader.FileUploaderContainer
          fileUploaderType={extraProps?.fileUploaderType}
          disabled={disabled}
          readonly={readonly}
          filesToUpload={value !== undefined ? value : []}
          onFilesUpdated={onFilesUpdated}
          onCancelUpload={onCancelUpload}
          allowAnyContentType={extraProps?.allowAnyContentType ?? false}
          allowAnyImageType={!extraProps?.allowedTypes?.length}
          allowedTypes={extraProps?.allowedTypes ?? [FileEnums.ContentTypes.ApplicationPDF]}
          maxFileSize={extraProps?.maxFileSize ?? MaxFileSize}
          singleSelection={extraProps?.singleSelection}
          maxFileCount={extraProps?.maxFileCount}
        />
      </FileViewerContainer>
    </>
  );
}

export default FileUploaderFormField;
